import { match } from 'ts-pattern';
import {
  ByteUnit,
  localizeByteUnit,
  localizeCompactCurrency,
  localizeCompactDateTime,
  localizeCurrency,
  localizeNumber,
  localizePercent,
  localizeRelativeTimeBetween,
} from './l10n';
import { isDefined } from './tsHelpers';

const KILOBYTE_INCREMENT = 1024;
const MEGABYTE_INCREMENT = KILOBYTE_INCREMENT ** 2;
const GIGABYTE_INCREMENT = KILOBYTE_INCREMENT ** 3;

const getByteUnitSize = (byteSize: number, unit: ByteUnit) => {
  switch (unit) {
    case ByteUnit.KB:
      return byteSize / KILOBYTE_INCREMENT;
    case ByteUnit.MB:
      return byteSize / MEGABYTE_INCREMENT;
    case ByteUnit.GB:
      return byteSize / GIGABYTE_INCREMENT;
    default:
      // ByteUnit.B
      return byteSize;
  }
};

const ROUND_THRESHOLD = 0.55;

const guessByteUnitSize = (byteSize: number) => {
  return match(byteSize)
    .when(
      (b) => b / GIGABYTE_INCREMENT > ROUND_THRESHOLD,
      () => ByteUnit.GB,
    )
    .when(
      (b) => b / MEGABYTE_INCREMENT > ROUND_THRESHOLD,
      () => ByteUnit.MB,
    )
    .when(
      (b) => b / KILOBYTE_INCREMENT > ROUND_THRESHOLD,
      () => ByteUnit.KB,
    )
    .otherwise(() => ByteUnit.B);
};

/**
 * Returns localized file size (string) and raw size (number) expressed in specified units
 * @param {string} bytes - the number of bytes (often a file size) to be localized
 * @param {string} [unit] - output size unit
 * @param {string} [locale] - any valid locale (will fallback to 'default' if invalid)
 * @returns {object} an object containing a localized, formatted string of the file size and a raw
 *   size number converted to the specified units (Example (en-US): '3,268.77 kB')
 */
export const localizeFileSize = (bytes = 0, unit = ByteUnit.KB, locale?: string) => {
  const actualUnit = unit === ByteUnit.AUTO ? guessByteUnitSize(bytes) : unit;
  const byteUnitSize = getByteUnitSize(bytes, actualUnit);

  return {
    formatted: localizeByteUnit(byteUnitSize, actualUnit, locale), // bytes
    size: byteUnitSize,
  };
};

// @deprecated: No replacement needed
export const getCurrentLocale = () =>
  navigator.languages && navigator.languages.length ? navigator.languages[0] : navigator.language;

// @deprecated: Use `localizeNumber` from `utils/i18n`
export const localizeInteger = (val: number | string, locale?: string) =>
  typeof val === 'number' ? localizeNumber(val, locale) : val;

export const maybeLocalizeInteger = (integer?: number) =>
  isDefined(integer) ? localizeInteger(integer) : undefined;

// @deprecated: Use `localizeCurrency` from `utils/i18n`
export const localizeCurrencyValue = (
  amount: number,
  { currency = 'USD' }: Intl.NumberFormatOptions = {},
) => localizeCurrency(amount, currency);

export const localizeCompactCurrencyValue = (
  amount: number,
  { currency = 'USD' }: Intl.NumberFormatOptions = {},
) => localizeCompactCurrency(amount, currency);

export const maybeLocalizeCurrencyValue = (integer?: number) =>
  isDefined(integer) ? localizeCurrencyValue(integer) : undefined;

// @deprecated: Use `localizePercent` from `utils/i18n`
export const localizePercentageValue = (percent: number, convertToFraction = true) =>
  localizePercent(convertToFraction ? percent / 100 : percent);

export const maybeLocalizePercentageValue = (integer?: number) =>
  isDefined(integer) ? localizePercentageValue(integer) : undefined;

// @deprecated: Use `localizeCompactDateTime` from `utils/i18n`
export const localizeDateTime = (date = new Date().toString(), locale = '') =>
  localizeCompactDateTime(new Date(date), locale);

/**
 * Returns a localized relative date/time offset and an actual date
 * @param {string} [date] - any valid date (as a string)
 * @param {string} [locale] - any valid locale
 * @returns {object} an object containing a localized relative date/time offset and an actual date (Example (en-US): '2 hours ago')
 */
export const localizeRelativeTime = (date = new Date().toString(), locale = '') => {
  const label = localizeRelativeTimeBetween(new Date(date), undefined, locale);
  const tooltip = localizeCompactDateTime(new Date(date), locale);

  return { label, tooltip };
};
